import qc from 'qc'
import { moveNodeByWorldPosition } from '@ps/bridge/qc/node'
import { rad2deg } from '@com/utils/unit-converters/angle'
import Konva from 'konva'
import { getHexColor, isItalic, transformColorFormat } from '@ps/utils/game'
import { RectSize } from '@ps/types/workbench/stage.type'
import store from '@ps/store'
import { Vector2d } from 'konva/lib/types'
import nodePool from '@ps/bridge/qc/node-pool'
import StageData from '@ps/store/workbench/stage/stage-data'
import {
  isEraseNode,
  isMaskComponentNode,
  isParkNode,
} from '@ps/bridge/qc/base'
import { isMaskShapeNode } from '@ps/workbench/undo-redo/module/mask'
import { isShelvesNode } from '@ps/workbench/undo-redo/module/shelves'

/**
 * 获取两个 dom 节点在视图内的 x/y 偏移量
 * @param parentId 父节点 id
 * @param sceneId 场景 id
 */
export function getOffset(parentId: string, sceneId: string): Vector2d {
  const parentDom = document.querySelector(`#${parentId}`) as HTMLElement
  const sceneDom = document.querySelector(`#${sceneId}`) as HTMLElement
  const stageDom = document.querySelector('#stage-container') as HTMLElement
  if (parentDom && sceneDom) {
    // 避免滚动条影响到获取偏移量错误
    stageDom.style.display = 'none'
    const parentRect = parentDom.getBoundingClientRect()
    const sceneRect = sceneDom.getBoundingClientRect()
    stageDom.style.display = 'block'
    return {
      x: sceneRect.left - parentRect.left,
      y: sceneRect.top - parentRect.top,
    }
  }
  return {
    x: 0,
    y: 0,
  }
}

/**
 * 根据节点与缩放信息转换成 konva.rect 格式
 * @param node qc.node
 * @param coordinate 当前画布与父级的坐标
 * @param sceneScale 当前画布的缩放
 */
export const convertNode2RectSize = (
  node: qc.Node,
  coordinate: Vector2d,
  sceneScale: number,
): RectSize => {
  const worldScale = store.state.workspace.game.device.worldScale
  // 因为组旋转会直接影响元素的旋转角度，因此 rotation 直接用坐标集合算出来的值而不用 node.rotation
  const rotation = node.getWorldRotation()
  const nodeWorldPosition = node.getWorldPosition()
  const nodeWorldScale = {
    x: node.getWorldScale().x / worldScale.x,
    y: node.getWorldScale().y / worldScale.y,
  }
  const width = node.width * sceneScale * worldScale.x
  const height = node.height * sceneScale * worldScale.y
  return {
    x: nodeWorldPosition.x * sceneScale + coordinate.x,
    y: nodeWorldPosition.y * sceneScale + coordinate.y,
    offset: {
      x: node.pivotX * width,
      y: node.pivotY * height,
    },
    width: width,
    height: height,
    scaleX: nodeWorldScale.x,
    scaleY: nodeWorldScale.y,
    rotation: rad2deg(rotation),
  }
}

/**
 * 根据 konva.rect 的中心点转换成 node 的世界坐标， 如果 pivotX/Y 不是 0.5 时，世界坐标不是 Rect 的中点，需要算出来
 * @param shape Konva.Rect
 * @param coordinate 当前画布与父级的坐标
 * @param sceneScale 当前画布的缩放
 */
export const convertShapePivot2NodeWorldPosition = (
  shape: Konva.Rect,
  coordinate: Vector2d,
  sceneScale: number,
): {
  x: number
  y: number
} => {
  return {
    x: (shape.x() - coordinate.x) / sceneScale,
    y: (shape.y() - coordinate.y) / sceneScale,
  }
}

/**
 * konva 的 rotation 在 > 180 的时候会显示负数，需要转换成 0-359 的范围
 * @param rotation konva 的旋转角度
 */
export const covertShapeRotation2Node = (rotation: number): number => {
  if (rotation < 0) {
    return rotation + 360
  }
  return rotation
}

// 把 node 与 shape 的节点信息转换成 dom 信息

export const convertNode2Dom = (
  dom: HTMLElement,
  node: qc.UIText,
  shape: Konva.Rect,
  sceneScale: number,
  worldScale: number,
  // eslint-disable-next-line max-params
): void => {
  const alignHMap: {
    [key: number]: string
  } = {
    0: 'left',
    1: 'right',
    2: 'center',
    3: 'justify',
  }
  const alignVMap: {
    [key: number]: string
  } = {
    0: 'flex-start',
    1: 'flex-end',
    2: 'center',
    3: 'justify',
  }
  const getDecorationLine = (node: qc.UIText): string => {
    let textDecoration = ''
    if (node.enableUnderline) {
      textDecoration += ' underline'
    }
    if (node.enableDeleteline) {
      textDecoration += ' line-through'
    }
    if (!textDecoration) {
      textDecoration = 'none'
    }
    return textDecoration
  }
  const setTextColor = (node: qc.UIText, dom: HTMLElement): void => {
    let colorValue
    // 没有渐变色的情况
    if (!node.gradient) {
      colorValue = transformColorFormat(node.color)
      dom.style.color = colorValue
    } else {
      const startColor = getHexColor(transformColorFormat(node.startColor))
      const endColor = getHexColor(transformColorFormat(node.endColor))
      colorValue = `linear-gradient(180deg, ${startColor}, ${endColor})`
      dom.style.backgroundImage = colorValue
      dom.style.webkitBackgroundClip = 'text'
      dom.style.color = 'transparent'
    }
  }
  dom.innerHTML = node.text
  dom.contentEditable = 'true'
  dom.className = 'text-editor'
  dom.style.top = shape.getClientRect().y + 'px'
  dom.style.left = shape.getClientRect().x + 'px'
  dom.style.width = shape.width() + 'px'
  dom.style.height = shape.height() + 'px'
  dom.style.fontSize = node.fontSize * sceneScale * worldScale + 'px'
  dom.style.fontWeight = node.bold ? 'bold' : 'normal'
  dom.style.fontStyle = isItalic(node) ? 'italic' : 'normal'
  dom.style.textDecorationLine = getDecorationLine(node)
  setTextColor(node, dom)
  dom.style.fontFamily = node.font as string // 字体
  dom.style.textAlign = alignHMap[node.alignH]
  dom.style.alignItems = alignVMap[node.alignV]
  dom.style.letterSpacing = node.letterSpacing + 'px'
  dom.style.lineHeight = String(node.lineHeight)
  const rotation = rad2deg(node.rotation)
  let transform = ''
  if (rotation) {
    transform += 'rotateZ(' + rotation + 'deg)'
  }
  dom.style.transform = transform
}

/* 光标处插入html代码，参数是String类型的html代码，例子："<p>猪头诺</p>"*/
export const insertHtml = (html: string): void => {
  let sel, range
  if (window.getSelection) {
    sel = window.getSelection()
    if (sel && sel.getRangeAt && sel.rangeCount) {
      range = sel.getRangeAt(0)
      range.deleteContents()
      // Range.createContextualFragment() would be useful here but is
      // non-standard and not supported in all browsers (IE9, for one)
      const el = document.createElement('div')
      el.innerHTML = html
      const frag = document.createDocumentFragment()
      let node
      let lastNode
      while ((node = el.firstChild)) {
        lastNode = frag.appendChild(node)
      }
      range.insertNode(frag)
      // Preserve the selection
      if (lastNode) {
        range = range.cloneRange()
        range.setStartAfter(lastNode)
        range.collapse(true)
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }
  }
}

/**
 * 通过 konva 的 shape 位置去更新 qcNode 的世界坐标位置
 * @param node qc.Node
 * @param shape konva 的 shape
 */
export function moveNodeByShape({
  node,
  shape,
}: {
  node: qc.Node
  shape: Konva.Rect
}): void {
  const sceneScale: number = store.state.workspace.game.device.scale
  const sceneOffset: Vector2d = store.state.workspace.stage.sceneOffset
  const { x, y } = convertShapePivot2NodeWorldPosition(
    shape,
    sceneOffset,
    sceneScale,
  )
  moveNodeByWorldPosition({
    node: node,
    x,
    y,
  })
}

export function getNodeByShape(shape: Konva.Rect): qc.Node {
  const uuid = shape.attrs.id
  return nodePool.find(uuid)
}

export function getShapeByNode(node: qc.Node): Konva.Rect {
  return StageData.layer!.findOne(`#${node.uuid}`)!
}

export function haveFatherSonRelationship(
  father: Konva.Rect,
  son: Konva.Rect,
): boolean {
  const fatherNode = getNodeByShape(father)
  let sonNode = getNodeByShape(son)
  while (sonNode) {
    if (sonNode === fatherNode) {
      return true
    }
    sonNode = sonNode.parent
  }
  return false
}

export function haveIntersection({
  shape,
  Vector,
}: {
  shape: Konva.Rect
  Vector: Vector2d
}): boolean {
  const clientRect = shape.getClientRect()
  return (
    clientRect.x + clientRect.width > Vector.x &&
    clientRect.y + clientRect.height > Vector.y &&
    clientRect.x < Vector.x &&
    clientRect.y < Vector.y
  )
}
// 根据节点图层的类型更新边框颜色
export function updateShapeBorderStyle(node: qc.Node) {
  if (
    isEraseNode(node) ||
    isParkNode(node) ||
    isMaskComponentNode(node) ||
    isShelvesNode(node)
  ) {
    // 擦除组件节点、停车解密组件、遮罩组件边框显示绿色
    if (StageData.transformer) {
      StageData.transformer.borderStroke('#52C41A')
      StageData.transformer.anchorStroke('#52C41A')
    }
  } else if (isMaskShapeNode(node)) {
    // 遮罩形状边框显示橙色
    if (StageData.transformer) {
      StageData.transformer.borderStroke('#F3AD02')
      StageData.transformer.anchorStroke('#F3AD02')
    }
    console.log(StageData.selectionRectangle)
  } else {
    if (StageData.transformer) {
      StageData.transformer.borderStroke('rgba(0,0,0,0.1)')
      StageData.transformer.anchorStroke('rgba(0,0,0,0.1)')
    }
  }
}
